import React, { useEffect, useMemo, useState } from "react";
import {
  Button,
  FormControl,
  Grid,
  InputLabel,
  ListSubheader,
  MenuItem,
  Select,
  Skeleton,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { Box } from "@mui/system";
import Chart from "react-apexcharts";
import DownloadIcon from "@mui/icons-material/Download";
import FilterAltOffOutlinedIcon from "@mui/icons-material/FilterAltOffOutlined";
import GroupsOutlinedIcon from "@mui/icons-material/GroupsOutlined";
import PlaceOutlinedIcon from "@mui/icons-material/PlaceOutlined";
import CallOutlinedIcon from "@mui/icons-material/CallOutlined";
import TrendingUpOutlinedIcon from "@mui/icons-material/TrendingUpOutlined";
import EventAvailableOutlinedIcon from "@mui/icons-material/EventAvailableOutlined";
import AccessTimeOutlinedIcon from "@mui/icons-material/AccessTimeOutlined";
import AssignmentTurnedInOutlinedIcon from "@mui/icons-material/AssignmentTurnedInOutlined";
import { Toaster } from "react-hot-toast";

import AccessController from "_shared/AccessController";
import { DashGridL2 } from "layouts/dash-grid";
import TopBar from "_shared/Top";
import {
  PageHeader,
  SectionCard,
  StatCard,
  EmptyState,
  ThemedDataGrid,
  useChartBase,
  mergeChartOptions,
} from "_shared/ui";
import { toastMessage } from "utils/toastMessage";
import {
  getSeAnalyticsFilters,
  getSeAnalyticsSummary,
  getSeAnalyticsTrend,
  getSeAnalyticsUsers,
  downloadSeAnalyticsReport,
} from "_controllers/officer";

// Visit-type categories (must match the backend category keys).
const CATEGORY_COLUMNS = [
  { key: "DEALER", label: "Dealer" },
  { key: "FLEET_DIRECT", label: "Direct Fleet" },
  { key: "FLEET_INDIRECT", label: "Indirect Fleet" },
  { key: "INFLUENCER", label: "Influencer" },
  { key: "OEM", label: "OEM" },
  { key: "TYREFITTER", label: "Tyre Fitter" },
  { key: "WEIGHBRIDGE", label: "Weighbridge" },
  { key: "CUSTOMER", label: "Customer" },
  { key: "PROSPECT", label: "Prospect" },
];

// Per-SE call-type columns (obs 9). `key` matches the backend callTypes keys.
const CALL_TYPE_COLUMNS = [
  { key: "Depot Claim Inspection", label: "Depot Claim" },
  { key: "THD Inspection", label: "THD" },
  { key: "Legal", label: "Legal" },
  { key: "Self Training/Learning", label: "Self Training" },
  { key: "Internal Meeting/Review", label: "Internal Mtg" },
  { key: "Report Preparation", label: "Report Prep" },
  { key: "Joint Inspection", label: "Joint Insp" },
  { key: "Fitment Survey", label: "Fitment" },
  { key: "Promotional Activities", label: "Promotions" },
  { key: "External Meeting", label: "External Mtg" },
];

// Unified Call-Type filter values. "" = all; the two sentinels = all-visits /
// all-calls; otherwise a visit-category key or a daily-call type string. These
// must match the backend (utils/seAnalytics.js: TYPE_ALL_VISITS / TYPE_ALL_CALLS).
const TYPE_ALL = "";
const TYPE_ALL_VISITS = "__VISITS__";
const TYPE_ALL_CALLS = "__CALLS__";
const CATEGORY_KEYS = CATEGORY_COLUMNS.map((c) => c.key);
const CALL_TYPE_KEYS = CALL_TYPE_COLUMNS.map((c) => c.key);

// Collapse a Call-Type selection into the display mode the cards/charts already
// branch on ("all" | "visits" | "calls").
const typeMode = (type) => {
  if (!type || type === TYPE_ALL) return "all";
  if (type === TYPE_ALL_VISITS || CATEGORY_KEYS.includes(type)) return "visits";
  if (type === TYPE_ALL_CALLS || CALL_TYPE_KEYS.includes(type)) return "calls";
  return "all";
};

// Friendly zone names for the "by zone" chart (obs 6: WZ -> West, …).
const ZONE_LABELS = {
  WZ: "West",
  NZ: "North",
  SZ: "South",
  EZ: "East",
  CZ: "Central",
};
const zoneLabel = (z) => ZONE_LABELS[z] || z;

// Full forms for the dealer `clubCategory` codes stored from SAP (ClubCatg).
// Keyed by the uppercased code; unknown codes fall through to the raw value.
const CLUB_CATEGORY_LABELS = {
  GP: "Gold Plus Club",
  NS: "NS Club",
  ND: "ND Club",
  BC: "Blue Club",
  DC: "Diamond Club",
  CH: "Chairman",
  PL: "Platinum Club",
  CA: "Chairman's Advisory Club",
  NA: "NOTA Club",
  // AH, LS, ST have no confirmed full form yet, so they fall back to the raw code.
};
const clubLabel = (code) => {
  const key = (code ?? "").toString().trim().toUpperCase();
  return CLUB_CATEGORY_LABELS[key] || code || "Uncategorized";
};

// YYYY-MM-DD helpers (local time).
const fmt = (d) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};
const firstOfMonth = () => {
  const d = new Date();
  return fmt(new Date(d.getFullYear(), d.getMonth(), 1));
};
const today = () => fmt(new Date());

// Format whole minutes as a compact "Xh Ym" / "Xh" / "Ym" label (obs 13).
const fmtMins = (mins) => {
  if (mins == null || !Number.isFinite(Number(mins))) return "—";
  const m = Math.max(0, Math.round(Number(mins)));
  const h = Math.floor(m / 60);
  const rem = m % 60;
  if (h > 0 && rem > 0) return `${h}h ${rem}m`;
  if (h > 0) return `${h}h`;
  return `${rem}m`;
};

const hasData = (values) =>
  Array.isArray(values) &&
  values.some((v) => v != null && Number.isFinite(Number(v)) && Number(v) > 0);

const ChartSlot = ({ children }) => (
  <Box sx={{ height: 340, display: "flex", flexDirection: "column", justifyContent: "center" }}>
    {children}
  </Box>
);

// Donut options: quantity shown on each slice, percentage shown on hover (obs 4).
const makeDonutOptions = (base, labels, series, totalLabel) => {
  const total = series.reduce((a, b) => a + Number(b || 0), 0);
  return mergeChartOptions(base, {
    labels,
    legend: { position: "bottom" },
    dataLabels: {
      enabled: true,
      // `val` is the percentage; render the absolute count instead.
      formatter: (val, opts) => `${opts.w.config.series[opts.seriesIndex]}`,
      dropShadow: { enabled: false },
    },
    tooltip: {
      y: {
        formatter: (val) => {
          const pct = total ? ((val / total) * 100).toFixed(1) : 0;
          return `${val} (${pct}%)`;
        },
      },
    },
    plotOptions: {
      pie: {
        donut: {
          labels: {
            show: true,
            total: { show: true, label: totalLabel, formatter: () => `${total}` },
          },
        },
      },
    },
  });
};

const Main = () => {
  const base = useChartBase();

  // Filters
  const [zone, setZone] = useState("");
  const [region, setRegion] = useState("");
  const [sapId, setSapId] = useState("");
  const [type, setType] = useState(TYPE_ALL); // unified Call-Type filter
  const activity = typeMode(type); // display mode derived from the filter
  const [groupBy, setGroupBy] = useState("day");
  const [startDate, setStartDate] = useState(firstOfMonth());
  const [endDate, setEndDate] = useState(today());

  // Filter option lists
  const [filterOptions, setFilterOptions] = useState({ zones: [], regions: [], users: [] });

  // Data
  const [summary, setSummary] = useState(null);
  const [trend, setTrend] = useState([]);
  const [rows, setRows] = useState([]);
  const [totalRows, setTotalRows] = useState(0);
  const [page, setPage] = useState(0); // MUI DataGrid is 0-indexed
  const [pageSize, setPageSize] = useState(10);

  // Loaders
  const [loadingSummary, setLoadingSummary] = useState(true);
  const [loadingTrend, setLoadingTrend] = useState(true);
  const [loadingTable, setLoadingTable] = useState(true);

  const [downloading, setDownloading] = useState(false);

  const showVisits = activity !== "calls";
  const showCalls = activity !== "visits";
  // The Dealer-by-category chart is dealer-specific, so only surface it when the
  // filter actually includes dealer visits.
  const showDealerCategory =
    type === TYPE_ALL || type === TYPE_ALL_VISITS || type === "DEALER";

  const filters = useMemo(
    () => ({ zone, region, sapId, type, start: startDate, end: endDate }),
    [zone, region, sapId, type, startDate, endDate]
  );

  // Load filter dropdowns once.
  useEffect(() => {
    (async () => {
      try {
        const data = await getSeAnalyticsFilters();
        if (data) setFilterOptions(data);
      } catch (err) {
        console.log("filters error", err);
      }
    })();
  }, []);

  // Summary
  useEffect(() => {
    (async () => {
      setLoadingSummary(true);
      try {
        const data = await getSeAnalyticsSummary(filters);
        setSummary(data || null);
      } catch (err) {
        console.log("summary error", err);
        setSummary(null);
      } finally {
        setLoadingSummary(false);
      }
    })();
  }, [filters]);

  // Trend
  useEffect(() => {
    (async () => {
      setLoadingTrend(true);
      try {
        const data = await getSeAnalyticsTrend({ ...filters, groupBy });
        setTrend(data?.trend || []);
      } catch (err) {
        console.log("trend error", err);
        setTrend([]);
      } finally {
        setLoadingTrend(false);
      }
    })();
  }, [filters, groupBy]);

  // Per-SE table
  useEffect(() => {
    (async () => {
      setLoadingTable(true);
      try {
        const res = await getSeAnalyticsUsers({ ...filters, page: page + 1, limit: pageSize });
        setRows(res?.data || []);
        setTotalRows(res?.totalData || 0);
      } catch (err) {
        console.log("table error", err);
        setRows([]);
        setTotalRows(0);
      } finally {
        setLoadingTable(false);
      }
    })();
  }, [filters, page, pageSize]);

  // Reset to first page whenever filters change.
  useEffect(() => {
    setPage(0);
  }, [filters]);

  // Reset every filter back to its default (current month, no zone/region/SE).
  const handleClearFilters = () => {
    setZone("");
    setRegion("");
    setSapId("");
    setType(TYPE_ALL);
    setGroupBy("day");
    setStartDate(firstOfMonth());
    setEndDate(today());
  };

  // True when any filter differs from its default — used to enable the Clear button.
  const filtersActive =
    !!zone ||
    !!region ||
    !!sapId ||
    type !== TYPE_ALL ||
    groupBy !== "day" ||
    startDate !== firstOfMonth() ||
    endDate !== today();

  const handleDownload = async () => {
    if (startDate && endDate && new Date(startDate) > new Date(endDate)) {
      toastMessage("Start Date should be less than End Date.", "error");
      return;
    }
    setDownloading(true);
    try {
      const blob = await downloadSeAnalyticsReport(filters);
      const url = window.URL.createObjectURL(new Blob([blob]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `SE_Analytics_${startDate}_to_${endDate}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toastMessage("Report downloaded successfully!", "success");
    } catch (err) {
      console.log("download error", err);
      toastMessage("Failed to download report.", "error");
    } finally {
      setDownloading(false);
    }
  };

  const totals = summary?.totals || {};
  const averages = summary?.averages || {};
  const byType = summary?.byType || [];
  const byZone = summary?.byZone || [];
  const byRegion = summary?.byRegion || [];
  // Chart title reflects the active toggle: both series, or a single one.
  const byZoneRegionLabel =
    activity === "all" ? "Visits & Calls" : activity === "visits" ? "Visits" : "Calls";
  // The trend is a single merged line — business treats visits + daily calls as
  // one "total" activity metric; the hover tooltip still breaks the two apart.
  const trendSeriesName =
    activity === "all"
      ? "Total (Visits + Calls)"
      : activity === "visits"
      ? "Visits"
      : "Calls";
  // Merged "Avg Time" card: business treats visits + daily calls as one metric,
  // so the two avg-time KPIs collapse into one (sum), split out on hover.
  const avgVisitMins = Number(averages.avgTimeSpentPerVisitMins) || 0;
  const avgCallMins = Number(averages.avgTimeSpentPerCallMins) || 0;
  const avgTimeMerged =
    (showVisits ? avgVisitMins : 0) + (showCalls ? avgCallMins : 0);
  const avgTimeLabel =
    activity === "all"
      ? "Avg Time (Visit + Call)"
      : activity === "visits"
      ? "Avg Time / Visit"
      : "Avg Time / Call";
  // Single-bar total per zone/region (visits + calls, honouring the toggle).
  const zoneRegionTotal = (d) =>
    (showVisits ? Number(d?.visits) || 0 : 0) + (showCalls ? Number(d?.calls) || 0 : 0);
  // Custom hover tooltip that reveals the visits/calls split behind the merged bar.
  const zoneRegionTooltip = (list) => ({ dataPointIndex }) => {
    const d = list[dataPointIndex] || {};
    const v = Number(d.visits) || 0;
    const c = Number(d.calls) || 0;
    const label = d.zone !== undefined ? zoneLabel(d.zone) : d.region || "";
    const line = (name, val, bold) =>
      `<div style="display:flex;justify-content:space-between;gap:16px;${
        bold ? "font-weight:600;" : ""
      }"><span>${name}</span><span>${val}</span></div>`;
    const body =
      activity === "all"
        ? line("Total", v + c, true) + line("Visits", v) + line("Calls", c)
        : activity === "visits"
        ? line("Visits", v, true)
        : line("Calls", c, true);
    return `<div class="apexcharts-tooltip-title" style="font-weight:600;">${label}</div><div style="padding:6px 10px;font-size:12px;min-width:150px;">${body}</div>`;
  };
  const byCallType = summary?.byCallType || [];
  const byDealerClubCategory = summary?.byDealerClubCategory || [];
  const inspections = summary?.inspections || {};
  const depot = inspections.depot || {};
  const thd = inspections.thd || {};

  // DataGrid columns (obs 9): visit categories, then per-call-type counts, then
  // a merged "Visits / Calls" total and a merged "Avg / Day" moved to the end.
  const columns = useMemo(
    () => [
      { field: "SAP_ID", headerName: "Emp Code", width: 110 },
      { field: "name", headerName: "Employee", width: 160 },
      { field: "zone", headerName: "Zone", width: 90 },
      { field: "regionName", headerName: "Region", width: 130 },
      ...CATEGORY_COLUMNS.map((c) => ({
        field: c.key,
        headerName: c.label,
        width: 110,
        type: "number",
      })),
      ...CALL_TYPE_COLUMNS.map((c) => ({
        field: `call_${c.key}`,
        headerName: c.label,
        width: 120,
        type: "number",
        valueGetter: (params) => Number(params.row?.callTypes?.[c.key]) || 0,
      })),
      {
        field: "totalVisitsCalls",
        headerName: "Total Visits + Calls",
        width: 160,
        type: "number",
        valueGetter: (params) =>
          (Number(params.row?.totalVisits) || 0) + (Number(params.row?.totalCalls) || 0),
      },
      { field: "workingDays", headerName: "Working Days", width: 120, type: "number" },
      {
        field: "avgVisitsCallsPerDay",
        headerName: "Avg (Visits + Calls)/Day",
        width: 190,
        type: "number",
        valueGetter: (params) => {
          const w = Number(params.row?.workingDays) || 0;
          const v = Number(params.row?.totalVisits) || 0;
          const c = Number(params.row?.totalCalls) || 0;
          return w > 0 ? Number(((v + c) / w).toFixed(1)) : 0;
        },
      },
    ],
    []
  );

  return (
    <>
      <PageHeader
        title="SE Analytics"
        subtitle="Service Engineer field activity — visits, calls, coverage & averages"
      />

      {/* Filter bar */}
      <SectionCard
        title="Filters"
        action={
          <Box sx={{ display: "flex", gap: 1 }}>
            <Button
              variant="outlined"
              size="small"
              onClick={handleClearFilters}
              disabled={!filtersActive}
              startIcon={<FilterAltOffOutlinedIcon />}
            >
              Clear Filters
            </Button>
            <Button
              variant="contained"
              size="small"
              onClick={handleDownload}
              disabled={downloading}
              endIcon={<DownloadIcon />}
            >
              {downloading ? "Downloading..." : "Download Excel"}
            </Button>
          </Box>
        }
      >
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={6} md={3}>
            <FormControl size="small" fullWidth>
              <InputLabel>Call Type</InputLabel>
              <Select label="Call Type" value={type} onChange={(e) => setType(e.target.value)}>
                <MenuItem value={TYPE_ALL}>All (Visits + Calls)</MenuItem>
                <MenuItem value={TYPE_ALL_VISITS}>All Visits</MenuItem>
                <MenuItem value={TYPE_ALL_CALLS}>All Calls</MenuItem>
                <ListSubheader>Visit types</ListSubheader>
                {CATEGORY_COLUMNS.map((c) => (
                  <MenuItem key={c.key} value={c.key} sx={{ pl: 3 }}>
                    {c.label}
                  </MenuItem>
                ))}
                <ListSubheader>Call types</ListSubheader>
                {CALL_TYPE_COLUMNS.map((c) => (
                  <MenuItem key={c.key} value={c.key} sx={{ pl: 3 }}>
                    {c.key}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <FormControl size="small" fullWidth>
              <InputLabel>Zone</InputLabel>
              <Select label="Zone" value={zone} onChange={(e) => setZone(e.target.value)}>
                <MenuItem value="">All Zones</MenuItem>
                {filterOptions.zones.map((z) => (
                  <MenuItem key={z} value={z}>
                    {zoneLabel(z)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <FormControl size="small" fullWidth>
              <InputLabel>Region</InputLabel>
              <Select label="Region" value={region} onChange={(e) => setRegion(e.target.value)}>
                <MenuItem value="">All Regions</MenuItem>
                {filterOptions.regions.map((r) => (
                  <MenuItem key={r.regionCode} value={r.regionCode}>
                    {r.regionName}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <FormControl size="small" fullWidth>
              <InputLabel>SE User</InputLabel>
              <Select label="SE User" value={sapId} onChange={(e) => setSapId(e.target.value)}>
                <MenuItem value="">All SEs</MenuItem>
                {filterOptions.users.map((u) => (
                  <MenuItem key={u.SAP_ID} value={u.SAP_ID}>
                    {u.name ? `${u.name} (${u.SAP_ID})` : u.SAP_ID}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={6} sm={3} md={2}>
            <TextField
              size="small"
              label="Start Date"
              type="date"
              fullWidth
              InputLabelProps={{ shrink: true }}
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
            />
          </Grid>
          <Grid item xs={6} sm={3} md={2}>
            <TextField
              size="small"
              label="End Date"
              type="date"
              fullWidth
              InputLabelProps={{ shrink: true }}
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
            />
          </Grid>
        </Grid>
      </SectionCard>

      {/* KPI row (obs 3 merged cards, obs 13 avg-time cards) */}
      <Grid container spacing={2.5} sx={{ mt: 0.5 }}>
        <Grid item xs={12} sm={6} md>
          <StatCard
            label={activity === "all" ? "Total Visits + Calls" : activity === "visits" ? "Total Visits" : "Total Calls"}
            value={
              loadingSummary
                ? "—"
                : activity === "all"
                ? (Number(totals.totalVisits) || 0) + (Number(totals.totalCalls) || 0)
                : activity === "visits"
                ? totals.totalVisits ?? 0
                : totals.totalCalls ?? 0
            }
            icon={activity === "calls" ? CallOutlinedIcon : PlaceOutlinedIcon}
            color="primary"
          />
        </Grid>
        <Grid item xs={12} sm={6} md>
          <StatCard
            label={
              activity === "all"
                ? "Avg (Visits + Calls)/Day"
                : activity === "visits"
                ? "Avg Visits/Day"
                : "Avg Calls/Day"
            }
            value={
              loadingSummary
                ? "—"
                : activity === "all"
                ? Number(((Number(averages.avgVisitsPerDay) || 0) + (Number(averages.avgCallsPerDay) || 0)).toFixed(1))
                : activity === "visits"
                ? averages.avgVisitsPerDay ?? 0
                : averages.avgCallsPerDay ?? 0
            }
            icon={TrendingUpOutlinedIcon}
            color="success"
          />
        </Grid>
        <Grid item xs={12} sm={6} md>
          <Tooltip
            arrow
            placement="top"
            title={
              loadingSummary ? (
                ""
              ) : (
                <Box>
                  {showVisits && (
                    <Typography variant="caption" display="block">
                      Avg Time / Visit: {fmtMins(averages.avgTimeSpentPerVisitMins)}
                    </Typography>
                  )}
                  {showCalls && (
                    <Typography variant="caption" display="block">
                      Avg Time / Call: {fmtMins(averages.avgTimeSpentPerCallMins)}
                    </Typography>
                  )}
                </Box>
              )
            }
          >
            <Box sx={{ height: "100%" }}>
              <StatCard
                label={avgTimeLabel}
                value={loadingSummary ? "—" : fmtMins(avgTimeMerged)}
                icon={AccessTimeOutlinedIcon}
                color="info"
              />
            </Box>
          </Tooltip>
        </Grid>
        <Grid item xs={12} sm={6} md>
          <StatCard
            label="Working Days"
            value={loadingSummary ? "—" : averages.workingDaysInPeriod ?? 0}
            icon={EventAvailableOutlinedIcon}
            color="warning"
          />
        </Grid>
        <Grid item xs={12} sm={6} md>
          <StatCard
            label="Active SEs"
            value={loadingSummary ? "—" : summary?.seCount ?? 0}
            icon={GroupsOutlinedIcon}
            color="error"
          />
        </Grid>
      </Grid>

      {/* Depot / THD claim-inspection productivity (obs 11, 12) */}
      {showCalls && (
        <Grid container spacing={2.5} sx={{ mt: 0.5 }}>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              label="Depot — Avg Claims Inspected"
              value={loadingSummary ? "—" : depot.avgClaimsInspected ?? 0}
              icon={AssignmentTurnedInOutlinedIcon}
              color="primary"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              label="Depot — Avg Time Spent"
              value={loadingSummary ? "—" : fmtMins(depot.avgTimeSpentMins)}
              icon={AccessTimeOutlinedIcon}
              color="secondary"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              label="THD — Avg Claims Inspected"
              value={loadingSummary ? "—" : thd.avgClaimsInspected ?? 0}
              icon={AssignmentTurnedInOutlinedIcon}
              color="primary"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              label="THD — Avg Time Spent"
              value={loadingSummary ? "—" : fmtMins(thd.avgTimeSpentMins)}
              icon={AccessTimeOutlinedIcon}
              color="secondary"
            />
          </Grid>
        </Grid>
      )}

      {/* Charts */}
      <Grid container spacing={2.5} sx={{ mt: 0.5 }}>
        {showVisits && (
          <Grid item xs={12} md={showCalls ? 6 : 12}>
            <SectionCard title="Visits by Type">
              <ChartSlot>
                {loadingSummary ? (
                  <Skeleton variant="rectangular" width="100%" height="100%" />
                ) : hasData(byType.map((t) => t.count)) ? (
                  <Chart
                    type="donut"
                    width="100%"
                    height="100%"
                    series={byType.map((t) => t.count)}
                    options={makeDonutOptions(
                      base,
                      byType.map((t) => t.label),
                      byType.map((t) => t.count),
                      "Total Visits"
                    )}
                  />
                ) : (
                  <EmptyState title="Visits by Type" description="No data available" />
                )}
              </ChartSlot>
            </SectionCard>
          </Grid>
        )}

        {showCalls && (
          <Grid item xs={12} md={showVisits ? 6 : 12}>
            <SectionCard title="Calls by Type">
              <ChartSlot>
                {loadingSummary ? (
                  <Skeleton variant="rectangular" width="100%" height="100%" />
                ) : hasData(byCallType.map((t) => t.count)) ? (
                  <Chart
                    type="donut"
                    width="100%"
                    height="100%"
                    series={byCallType.map((t) => t.count)}
                    options={makeDonutOptions(
                      base,
                      byCallType.map((t) => t.type),
                      byCallType.map((t) => t.count),
                      "Total Calls"
                    )}
                  />
                ) : (
                  <EmptyState title="Calls by Type" description="No data available" />
                )}
              </ChartSlot>
            </SectionCard>
          </Grid>
        )}

        <Grid item xs={12} md={6}>
          <SectionCard title={`${byZoneRegionLabel} by Zone`}>
            <ChartSlot>
              {loadingSummary ? (
                <Skeleton variant="rectangular" width="100%" height="100%" />
              ) : hasData(byZone.map((z) => zoneRegionTotal(z))) ? (
                <Chart
                  type="bar"
                  width="100%"
                  height="100%"
                  series={[
                    { name: trendSeriesName, data: byZone.map((z) => zoneRegionTotal(z)) },
                  ]}
                  options={mergeChartOptions(base, {
                    xaxis: { categories: byZone.map((z) => zoneLabel(z.zone)) },
                    plotOptions: { bar: { columnWidth: "55%", borderRadius: 4 } },
                    dataLabels: { enabled: true },
                    legend: { position: "top" },
                    tooltip: { followCursor: true, custom: zoneRegionTooltip(byZone) },
                  })}
                />
              ) : (
                <EmptyState title={`${byZoneRegionLabel} by Zone`} description="No data available" />
              )}
            </ChartSlot>
          </SectionCard>
        </Grid>

        <Grid item xs={12} md={6}>
          <SectionCard title={`${byZoneRegionLabel} by Region`}>
            <ChartSlot>
              {loadingSummary ? (
                <Skeleton variant="rectangular" width="100%" height="100%" />
              ) : hasData(byRegion.map((r) => zoneRegionTotal(r))) ? (
                <Chart
                  type="bar"
                  width="100%"
                  height="100%"
                  series={[
                    { name: trendSeriesName, data: byRegion.map((r) => zoneRegionTotal(r)) },
                  ]}
                  options={mergeChartOptions(base, {
                    xaxis: { categories: byRegion.map((r) => r.region) },
                    plotOptions: { bar: { columnWidth: "55%", borderRadius: 4 } },
                    dataLabels: { enabled: true },
                    legend: { position: "top" },
                    tooltip: { followCursor: true, custom: zoneRegionTooltip(byRegion) },
                  })}
                />
              ) : (
                <EmptyState title={`${byZoneRegionLabel} by Region`} description="No data available" />
              )}
            </ChartSlot>
          </SectionCard>
        </Grid>

        {showDealerCategory && (
          <Grid item xs={12}>
            <SectionCard title="Dealer Visits by Category">
              <ChartSlot>
                {loadingSummary ? (
                  <Skeleton variant="rectangular" width="100%" height="100%" />
                ) : hasData(byDealerClubCategory.map((d) => d.count)) ? (
                  <Chart
                    type="bar"
                    width="100%"
                    height="100%"
                    series={[
                      { name: "Dealer Visits", data: byDealerClubCategory.map((d) => d.count) },
                    ]}
                    options={mergeChartOptions(base, {
                      xaxis: { categories: byDealerClubCategory.map((d) => clubLabel(d.category)) },
                      plotOptions: { bar: { columnWidth: "55%", borderRadius: 4 } },
                      dataLabels: { enabled: true },
                      legend: { position: "top" },
                      tooltip: { followCursor: true },
                    })}
                  />
                ) : (
                  <EmptyState title="Dealer Visits by Category" description="No data available" />
                )}
              </ChartSlot>
            </SectionCard>
          </Grid>
        )}

        <Grid item xs={12}>
          <SectionCard
            title="Visits & Calls Trend"
            action={
              <FormControl size="small" sx={{ minWidth: 120 }}>
                <InputLabel>Group By</InputLabel>
                <Select
                  label="Group By"
                  value={groupBy}
                  onChange={(e) => setGroupBy(e.target.value)}
                >
                  <MenuItem value="day">Day</MenuItem>
                  <MenuItem value="month">Month</MenuItem>
                  <MenuItem value="year">Year</MenuItem>
                </Select>
              </FormControl>
            }
          >
            <ChartSlot>
              {loadingTrend ? (
                <Skeleton variant="rectangular" width="100%" height="100%" />
              ) : hasData([
                  ...trend.map((t) => t.visits),
                  ...trend.map((t) => t.calls),
                ]) ? (
                <Chart
                  type="area"
                  width="100%"
                  height="100%"
                  series={[
                    {
                      name: trendSeriesName,
                      data: trend.map(
                        (t) =>
                          (showVisits ? Number(t.visits) || 0 : 0) +
                          (showCalls ? Number(t.calls) || 0 : 0)
                      ),
                    },
                  ]}
                  options={mergeChartOptions(base, {
                    xaxis: { categories: trend.map((t) => t.period) },
                    stroke: { curve: "smooth", width: 2 },
                    fill: { type: "gradient", gradient: { opacityFrom: 0.4, opacityTo: 0.05 } },
                    dataLabels: { enabled: false },
                    legend: { position: "top" },
                    tooltip: {
                      followCursor: true,
                      // Single merged line, but reveal the visits/calls split on hover.
                      custom: ({ dataPointIndex }) => {
                        const t = trend[dataPointIndex] || {};
                        const v = Number(t.visits) || 0;
                        const c = Number(t.calls) || 0;
                        const line = (label, val, bold) =>
                          `<div style="display:flex;justify-content:space-between;gap:16px;${
                            bold ? "font-weight:600;" : ""
                          }"><span>${label}</span><span>${val}</span></div>`;
                        const body =
                          activity === "all"
                            ? line("Total", v + c, true) + line("Visits", v) + line("Calls", c)
                            : activity === "visits"
                            ? line("Visits", v, true)
                            : line("Calls", c, true);
                        return `<div class="apexcharts-tooltip-title" style="font-weight:600;">${
                          t.period || ""
                        }</div><div style="padding:6px 10px;font-size:12px;min-width:150px;">${body}</div>`;
                      },
                    },
                  })}
                />
              ) : (
                <EmptyState title="Visits & Calls Trend" description="No data available" />
              )}
            </ChartSlot>
          </SectionCard>
        </Grid>

        <Grid item xs={12}>
          <SectionCard title="Per-SE Breakdown">
            <ThemedDataGrid
              rows={rows}
              columns={columns}
              getRowId={(row) => row.SAP_ID}
              loading={loadingTable}
              paginationMode="server"
              rowCount={totalRows}
              page={page}
              onPageChange={(p) => setPage(p)}
              pageSize={pageSize}
              onPageSizeChange={(s) => setPageSize(s)}
              rowsPerPageOptions={[10, 25, 50, 100]}
              height={520}
            />
          </SectionCard>
        </Grid>
      </Grid>
    </>
  );
};

const SEAnalytics = () => {
  return (
    <AccessController roles={["ADMIN", "ADMINSE"]}>
      <DashGridL2 Top={TopBar} Main={Main} />
      <Toaster />
    </AccessController>
  );
};

export default SEAnalytics;
